import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { closeModal, showToastMessage } from 'enable-ui';
import { useFormik } from 'formik';
import { institutionDetailSelector, setIsRefresh, useStationStore } from 'stores/station';

import { updateUserProfile, useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import { Station } from '../type';

import { checkRole, hasEmptyValues, trimValue } from 'helpers';
import { apiCall } from 'hooks/service';
import { debounce, isEmpty, isEqual } from 'lodash';

import { PATH } from 'constants/path';

import { defaultStation } from '../constant';
import { StationSearchType } from '../enum';

export const useStationModalController = (stationId?: string) => {
  const location = useLocation();

  const userProfile = useAuthStore(userProfileSelector);

  const institutionDetail = useStationStore(institutionDetailSelector);

  const isStation = userProfile?.institution?.isStation;

  const isInstitutionDetail = location.pathname.includes(`${PATH.INSTITUTIONS}`);

  const hideCloseIcon = checkRole([RoleKey.INSTITUTION_ADMIN]) && !isStation;

  const [locationData, setLocationData] = useState({
    countryOptions: [] as { label: string; value: string }[],
    stateOptions: [] as { label: string; value: string }[],
    cityOptions: [] as { label: string; value: string }[],
    selectedCountry: null as { label: string; value: string } | null,
    selectedState: null as { label: string; value: string } | null,
    selectedCity: null as { label: string; value: string } | null,
  });

  const [selectedOption, setSelectedOption] = useState<string>();

  const [stationDetail, setStationDetail] = useState(defaultStation);

  const isLocationDataChanged =
    locationData.selectedCountry?.label !== stationDetail.country ||
    locationData.selectedState?.label !== stationDetail.state ||
    locationData.selectedCity?.label !== stationDetail.city;

  useEffect(() => {
    getStationInfo('', StationSearchType.COUNTRY);
  }, []);

  useEffect(() => {
    stationId
      ? getStationDetail(stationId)
      : setLocationData((prev) => ({
          ...prev,
          selectedCity: null,
          selectedState: null,
        }));
  }, [stationId]);

  const getStationDetail = async (id: string) => {
    const result: Station & { postal_code: null } = await apiCall({
      url: `/stations/${id}`,
      method: 'GET',
      isLoading: true,
      showError: true,
    });

    if (result?.id) {
      const formattedData = {
        ...result,
        id: result.id || '',
        postalCode: result.postal_code,
        city: result.city?.name || '',
        state: result.state?.name || '',
        country: result.country?.name || '',
      };

      setValues(formattedData);
      setStationDetail(formattedData);
      setLocationData((prev) => ({
        ...prev,
        selectedState: result.state ? { value: result.state.id, label: result.state.name } : null,
        selectedCity: result.city ? { value: result.city.id, label: result.city.name } : null,
      }));
    }
  };

  const updateLocationData = <T>(key: keyof typeof locationData, value: T) => {
    setLocationData((prev) => {
      let updatedData: typeof locationData = { ...prev, [key]: value };

      if (key === 'selectedCountry') {
        updatedData = {
          ...updatedData,
          selectedState: null,
          selectedCity: null,
          stateOptions: [],
          cityOptions: [],
        };
      }

      if (key === 'selectedState') {
        updatedData = {
          ...updatedData,
          selectedCity: null,
          cityOptions: [],
        };
      }

      return updatedData;
    });
  };

  const getStationInfo = async (search: string, typeSearch: StationSearchType) => {
    const result: { id: string; name: string }[] = await apiCall({
      url: `/stations/search`,
      method: 'GET',
      data: {
        type: typeSearch,
        search,
        stateId: locationData.selectedState?.value,
        countryId: locationData.selectedCountry?.value,
      },
    });

    if (isEmpty(result)) {
      return;
    }

    const typeMapping: Record<StationSearchType, keyof typeof locationData> = {
      [StationSearchType.COUNTRY]: 'countryOptions',
      [StationSearchType.STATE]: 'stateOptions',
      [StationSearchType.CITY]: 'cityOptions',
    };

    updateLocationData(
      typeMapping[typeSearch],
      result.map((el) => ({ value: el.id, label: el.name })),
    );

    if (typeSearch === StationSearchType.COUNTRY) {
      updateLocationData(
        'selectedCountry',
        result.map((el) => ({ value: el.id, label: el.name }))[0],
      );
    }
  };

  const formik = useFormik({
    initialValues: defaultStation,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      let result: { id: string };

      if (values.id) {
        result = await apiCall({
          url: `/stations/${values.id}`,
          method: 'PATCH',
          data: {
            name: values.name,
            address: values.address,
            postalCode: values.postalCode,
            countryId: locationData.selectedCountry?.value,
            cityId: locationData.selectedCity?.value,
            stateId: locationData.selectedState?.value,
            stationId: values.stationId,
            institutionId: isInstitutionDetail
              ? institutionDetail?.id
              : userProfile?.institution?.id,
          },
          isLoading: true,
          showError: true,
        });
      } else {
        result = await apiCall({
          url: `/stations`,
          method: 'POST',
          data: {
            stationId: values.stationId,
            name: values.name,
            address: values.address,
            postalCode: values.postalCode,
            countryId: locationData.selectedCountry?.value,
            cityId: locationData.selectedCity?.value,
            stateId: locationData.selectedState?.value,
            institutionId: isInstitutionDetail
              ? institutionDetail?.id
              : userProfile?.institution?.id,
          },
          isLoading: true,
          showError: true,
        });
      }

      if (!result.id) {
        return;
      }

      if (values.id) {
        showToastMessage('success', 'Updated center successfully!');
      } else {
        showToastMessage('success', 'Added center successfully!');

        updateUserProfile({
          ...userProfile,
          institution: {
            ...userProfile.institution,
            isStation: true,
          },
        });
      }

      setValues(defaultStation);

      closeModal(MODAL_KEY.ADD_STATION);

      setIsRefresh(true);
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, setValues, setFieldValue } = formik;

  const disabledAddButton =
    isSubmitting ||
    !isValid ||
    hasEmptyValues({ name: values.name, stationId: values.stationId }) ||
    !locationData.selectedCountry?.value;

  const disabledEditButton =
    isSubmitting ||
    !isValid ||
    hasEmptyValues({ name: values.name, stationId: values.stationId }) ||
    (isEqual(values, stationDetail) && !isLocationDataChanged);

  const disabledButton = values.id ? disabledEditButton : disabledAddButton;

  const onCloseModal = () => {
    closeModal(MODAL_KEY.ADD_STATION);
    setValues(defaultStation);
  };

  const handleFocus = useCallback(
    debounce((type: StationSearchType, value: string) => {
      getStationInfo(value, type);
      setSelectedOption(value);
    }, 500),
    [],
  );

  const handleChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: StationSearchType,
  ) => {
    setFieldValue(type, event.target.value);
    debounceSearch(event.target.value, type);
  };

  const debounceSearch = useCallback(
    debounce((newSearch: string, type: StationSearchType) => {
      getStationInfo(trimValue(newSearch), type);
    }, 500),
    [],
  );

  const handleChangeOption = (item: { label: string; key: string }, type: StationSearchType) => {
    setFieldValue(type, item.key);
    setSelectedOption(item.key);
  };

  const onFocus = (type: StationSearchType) => {
    getStationInfo('', type);
  };

  // const removeOptions = () => {
  //   setOptions([]);
  // };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    const name = event.target.name;

    if (name !== 'postalCode') {
      setFieldValue(name, value);

      return;
    }

    value = value.replace(/[^0-9.]/g, '');
    setFieldValue(name, value ? Number(value) : '');
  };

  return {
    values,
    // options,
    onCloseModal,
    handleChange: handleInputChange,
    handleSubmit,
    handleFocus,
    handleChangeInput,
    handleChangeOption,
    selectedOption,
    disabledButton,
    hideCloseIcon,
    // removeOptions,
    locationData,
    updateLocationData,
    onFocus,
  };
};
